import * as React from 'react';
import { connect } from 'react-redux';

import ActionButton from 'components/buttons/action-button/ActionButton';
import ActionLink from 'components/buttons/action-link/ActionLink';
import { withModal } from 'components/modal/ModalHOC';
import PageHeading from 'components/page/page-heading/PageHeading';
import PageSection from 'components/page/page-section/PageSection';
import SortableTable from 'components/sortable-table/SortableTable';
import Spinner from 'components/spinner/Spinner';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import Tooltip from 'components/Tooltip';
import { FormField } from 'components/form';
import RadioButtons from 'components/inputs/radio-buttons/RadioButtons';

import ProductListModal from '../product-list-modal/ProductListModal';

import {
  deleteCampaignProductList,
  fetchCampaignProductLists,
  fetchIsFlexibleSKUForCampaign,
  resetCampaignProductLists,
  toggleFlexibleSKUForCampaign } from '../../reducers/campaign-product-lists';
import { success } from 'redux-store/reducers/common-reducer';
import { hideConfirmModal, showConfirmModal } from 'redux-store/reducers/modal-reducer';

import { getDisplayDate } from 'utils/date';
import { copyTextToClipboard } from 'utils/helper/common';
import { __t, applyTranslations } from 'utils/language';

import type { CampaignProductList, SortableTableColumnType } from 'types';

type DispatchProps = {
  dispatchDeleteProductList: (b: number, a: number) => Promise<void>,
  dispatchFetchCampaignProductLists: (a: number) => void,
  dispatchResetCampaignProductLists: () => void,
  dispatchShowConfirmationModal: (
    params: {
      message: string,
      title: string,
      confirmButtonLabel: string,
      onConfirm: () => void,
      onCancel: () => void
    }
  ) => void,
  dispatchHideConfirmationModal: () => void,
  dispatchSuccessMessage: (a: string) => void,
  dispatchfetchIsFlexibleSKUForCampaign: (a: number) => void,
  dispatchtoggleFlexibleSKUForCampaign: (b: number, a: boolean) => void
};

type OwnProps = {
  campaignId: number
};

type StateProps = {
  campaignProductLists: Array<CampaignProductList>,
  campaignProductListsLoading: boolean,
  isFlexible: boolean
};

type Props = OwnProps & StateProps & DispatchProps;

import './campaign-product-lists.scss';

applyTranslations(require('./campaign-product-lists.i18n.json'));

const ActionButtonWithModal = withModal(ActionButton, {
  modalContent: ProductListModal
});

const SVGDynamicIconWithModal = withModal(SVGDynamicIcon, {
  modalContent: ProductListModal
});

const getCopyProductListSelector = (id: number): string => `js-productListUrl-${id}`;

const columns: Array<SortableTableColumnType> = [
  {
    title: __t('campaign_product_lists.column.title'),
    key: 'title',
    width: 352,
    render: (col: CampaignProductList) => (
      <ActionLink
        link={col.sharedAccessLink}
        size="inherit"
        color="light"
        className="campaign-product-lists__link text-overflow-ellipsis"
        openInNewTab
        decoration="none"
      >
        {col.title}
      </ActionLink>
    )
  },
  {
    title: __t('campaign_product_lists.column.count'),
    key: 'count',
    width: 350,
    align: 'center',
    render: (col: CampaignProductList) => (
      <div className="campaign-product-lists__count">
        {col.itemsCount}
      </div>
    )
  },
  {
    title: __t('campaign_product_lists.column.dates'),
    key: 'dates',
    align: 'center',
    sort: (a: CampaignProductList, b: CampaignProductList) => a.updatedAt.localeCompare(b.updatedAt),
    render: (col: CampaignProductList) => (
      <div className="campaign-product-lists__date">
        {getDisplayDate(col.updatedAt)}
      </div>
    )
  }
];

const FLEXIBLE_SKU_OPTIONS = [
  {
    value: 'false',
    name: __t('campaign_product_lists.product_type_strict')
  },
  {
    value: 'true',
    name: __t('campaign_product_lists.product_type_flexible')
  }
];

class CampaignProductLists extends React.PureComponent<Props> {
  componentDidMount() {
    const { campaignId, dispatchFetchCampaignProductLists, dispatchfetchIsFlexibleSKUForCampaign } = this.props;
    dispatchFetchCampaignProductLists(campaignId);
    dispatchfetchIsFlexibleSKUForCampaign(campaignId);
  }

  componentWillUnmount() {
    const { dispatchResetCampaignProductLists } = this.props;
    dispatchResetCampaignProductLists();
  }

  copyLinkToClipboard = (id: number) => {
    const { dispatchSuccessMessage } = this.props;
    const isCopied = copyTextToClipboard(`#${getCopyProductListSelector(id)}`, false);

    if (isCopied) {
      dispatchSuccessMessage(__t('campaign_product_lists.link_copied_message'));
    }
  };

  deleteProductList = (campaignId: number, id: number) => {
    const { dispatchDeleteProductList, dispatchHideConfirmationModal, dispatchShowConfirmationModal } = this.props;

    dispatchShowConfirmationModal({
      title: __t('campaign_product_lists.list_delete_confirmation.title'),
      message: __t('campaign_product_lists.list_delete_confirmation.message'),
      confirmButtonLabel: __t('campaign_product_lists.list_delete_confirmation.button_confirm'),
      onConfirm: () =>
        dispatchDeleteProductList(campaignId, id)
          .then(() => dispatchHideConfirmationModal()), // todo show notification https://zpl.io/2ElNx5x
      onCancel: () => dispatchHideConfirmationModal()
    });
  };

  getActionColumn = (campaignId: number): SortableTableColumnType => ({
    title: __t('campaign_product_lists.column.actions'),
    key: 'actions',
    display: 'flex',
    width: 142,
    render: (col: CampaignProductList) => (
      <div className="campaign-product-lists__actions">
        <Tooltip
          width="short"
          message={__t('campaign_product_lists.tooltip.view')}
          classNameWrapper="campaign-product-lists__tooltip"
        >
          <SVGDynamicIconWithModal
            name="pencil-custom-bg"
            width={20}
            height={20}
            customColor="grey"
            clickable
            modalContentProps={{
              campaignId,
              listId: col.id
            }}
          />
        </Tooltip>
        <Tooltip
          width="short"
          message={__t('campaign_product_lists.tooltip.copy')}
          classNameWrapper="campaign-product-lists__tooltip"
        >
          <SVGDynamicIcon
            name="link-custom-bg"
            width={24}
            height={24}
            customColor="grey"
            onClick={() => this.copyLinkToClipboard(col.id)}
          />
          <div
            id={getCopyProductListSelector(col.id)}
            // @ts-ignore
            value={col.sharedAccessLink}
            className="campaign-product-lists__link-to-copy"
          >
            {`${window.location.origin}${col.sharedAccessLink}`}
          </div>
        </Tooltip>
        <Tooltip
          width="short"
          message={__t('campaign_product_lists.tooltip.messenger')}
          classNameWrapper="campaign-product-lists__tooltip"
        >
          <SVGDynamicIcon
            name="trash-custom-fg"
            width={20}
            height={20}
            customColor="grey"
            openInNewTab
            onClick={() => this.deleteProductList(campaignId, col.id)}
          />
        </Tooltip>
      </div>
    )
  });

  handleIsFlexibleChange = (value: string) => {
    const { dispatchtoggleFlexibleSKUForCampaign, campaignId, isFlexible } = this.props;
    if (value !== String(isFlexible)) {
      dispatchtoggleFlexibleSKUForCampaign(campaignId, !isFlexible);
    }
  };

  renderHeader = () => (
    <div className="campaign-product-lists__header">
      {__t('campaign_product_lists.subtitle')}
      <ActionButtonWithModal
        color="secondary"
        className="campaign-product-lists__button"
        modalContentProps={{
          campaignId: this.props.campaignId
        }}
      >
        {__t('campaign_product_lists.add_button')}
      </ActionButtonWithModal>
    </div>
  );

  renderProductListType = () => {
    const { isFlexible } = this.props;
    return (
      <div>
        <FormField
          label={__t('campaign_product_lists.product_type_title')}
          labelType="highlighted"
          direction="column"
          showRequiredMark
          tooltipWidth="extra-long"
          tooltipDirection={{
            vertical: 'top',
            horizontal: 'center'
          }}
          tooltipMessage={__t('campaign_product_lists.product_type_tooltip')}
        >
          <RadioButtons
            selected={String(!!isFlexible)}
            options={FLEXIBLE_SKU_OPTIONS}
            onChange={this.handleIsFlexibleChange}
            direction="row"
          />
        </FormField>
      </div>
    );
  };

  render() {
    const { campaignId, campaignProductLists, campaignProductListsLoading } = this.props;

    return (
      <React.Fragment>
        <PageHeading
          color="light"
          margin="none"
          heading={__t('campaign_product_lists.title')}
        />
        {this.renderHeader()}
        {this.renderProductListType()}
        <PageSection
          style="volume"
          padding="none"
          margin="none"
          customClass="campaign-product-lists__section"
        >
          {
            campaignProductListsLoading ?
              <div className="campaign-product-lists__spinner">
                <Spinner
                  position="inline"
                  size="small"
                  color="light"
                />
              </div> :
              <SortableTable
                layout="dense"
                data={campaignProductLists}
                columns={[
                  ...columns,
                  this.getActionColumn(campaignId)
                ]}
                emptyMessage={__t('campaign_product_lists.empty_message')}
                emptyMessageClassName="campaign-product-lists__empty-message"
              />
          }
        </PageSection>
      </React.Fragment>
    );
  }
}

const mapStateToProps = ({ campaignProductLists }): StateProps => ({
  campaignProductLists: campaignProductLists.items,
  campaignProductListsLoading: campaignProductLists.isLoading,
  isFlexible: campaignProductLists.isFlexible
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchDeleteProductList: (campaignId, id) => dispatch(deleteCampaignProductList(campaignId, id)),

  dispatchShowConfirmationModal: (params: {
    message: string
  }) => dispatch(showConfirmModal(params)),

  dispatchHideConfirmationModal: () => dispatch(hideConfirmModal()),
  dispatchFetchCampaignProductLists: (campaignId) => dispatch(fetchCampaignProductLists(campaignId)),
  dispatchResetCampaignProductLists: () => dispatch(resetCampaignProductLists()),
  dispatchSuccessMessage: (message) => dispatch(success(message, { hideDuration: 2000 })),
  dispatchfetchIsFlexibleSKUForCampaign: (campaignId) => dispatch(fetchIsFlexibleSKUForCampaign(campaignId)),
  dispatchtoggleFlexibleSKUForCampaign: (campaignId, isFlexible) => dispatch(toggleFlexibleSKUForCampaign(campaignId, isFlexible))
});

export default connect(mapStateToProps, mapDispatchToProps)(CampaignProductLists);
